"use client";

// Kartu KELOMPOK bundel di keranjang (mig 8040; rencana §6.3). Komponen tampil
// bersama pilihan variannya, tapi tak satu pun bisa diubah satu per satu —
// yang bisa hanya jumlah bundel, "Ubah pilihan" (kembali ke halaman bundel),
// dan "Hapus bundel". Baris komponen tak pernah melebur dengan baris produk
// biasa yang variannya sama (larik `bundles` terpisah dari `carts`).
//
// Harga: harga TERKINI untuk pembeli yang login (`useBundleCartPricing` →
// resolver checkout), potret keranjang untuk tamu / selama harga terkini
// dimuat. Rumus tampilannya satu: `bundleCardPrice` (`bundleCheckout.ts`).

import { Layers, Pencil, RefreshCw, Trash2 } from "lucide-react";
import Link from "next/link";

import { QuantityStepper } from "@/components/QuantityStepper";
import { StoreImage } from "@/components/StoreImage";
import { MAX_BUNDLE_QTY, type CartBundle } from "@/lib/bundleCart";
import { bundleCardPrice, bundleCardUnitTotal, type BundleLiveState } from "@/lib/bundleCheckout";
import { priceExtraNote } from "@/lib/priceExtraLabel";
import { formatPrice } from "@/lib/products";

export function BundleCartCard({
  bundle,
  live,
  refreshing = false,
  onQtyChange,
  onRemove,
  onRetry
}: {
  bundle: CartBundle;
  /** Harga terkini bundel ini; undefined = belum ada (tamu / sedang dimuat) → potret keranjang. */
  live: BundleLiveState | undefined;
  /** Harga terkini sedang dihitung ulang. */
  refreshing?: boolean;
  onQtyChange: (qty: number) => void;
  onRemove: () => void;
  onRetry?: () => void;
}) {
  const price = bundleCardPrice(bundle, live);
  const unit = bundleCardUnitTotal(price);
  const typeLabel = bundle.productType === "preorder" ? "Pre-Order" : "Ready Stock";

  return (
    <div className="rounded-xl border border-shopee/30 p-3">
      {/* Kepala: nama bundel + hapus */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <span className="inline-flex items-center gap-1 rounded-md bg-shopeeSoft/60 px-1.5 py-0.5 text-[10px] font-extrabold text-shopee">
            <Layers size={11} />
            Bundling · {typeLabel}
          </span>
          <h3 className="mt-1 line-clamp-2 text-sm font-extrabold leading-5 text-ink">{bundle.name}</h3>
          <p className="text-[11px] font-semibold text-muted">
            {bundle.choices.length} barang per bundling
          </p>
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-muted"
        >
          <Trash2 size={11} />
          Hapus bundling
        </button>
      </div>

      {/* Isi bundel — tampilan saja, tak bisa diubah satu per satu */}
      <ul className="mt-3 space-y-2 border-t border-neutral-100 pt-3">
        {bundle.choices.map((c) => (
          <li key={c.componentId} className="flex items-center gap-3">
            <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-shopeeSoft">
              <StoreImage
                src={c.productImage}
                alt={c.productName}
                width={48}
                height={48}
                sizes="48px"
                className="h-12 w-12 object-cover"
              />
            </div>
            <div className="min-w-0 flex-1">
              {c.label ? (
                <p className="text-[10px] font-bold uppercase tracking-wide text-muted">{c.label}</p>
              ) : null}
              <p className="line-clamp-1 text-xs font-bold text-ink">{c.productName}</p>
              <p className="line-clamp-1 text-[11px] font-semibold text-muted">{c.variationName}</p>
            </div>
            <span className="shrink-0 text-[11px] font-bold text-muted">×{bundle.qty}</span>
          </li>
        ))}
      </ul>

      {/* Harga per bundel */}
      <div className="mt-3 border-t border-neutral-100 pt-3">
        {price.kind === "error" ? (
          <div className="rounded-lg bg-shell p-2.5">
            <p className="text-xs font-bold text-shopee">{price.message}</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {onRetry ? (
                <button
                  type="button"
                  onClick={onRetry}
                  className="inline-flex items-center gap-1 rounded-lg bg-white px-2.5 py-1.5 text-[11px] font-bold text-ink shadow-sm"
                >
                  <RefreshCw size={11} /> Coba lagi
                </button>
              ) : null}
              <button
                type="button"
                onClick={onRemove}
                className="inline-flex items-center gap-1 rounded-lg bg-shopee px-2.5 py-1.5 text-[11px] font-extrabold text-white shadow-sm"
              >
                <Trash2 size={11} /> Hapus bundling
              </button>
            </div>
          </div>
        ) : price.kind === "separate" ? (
          <div>
            <p className="text-sm font-extrabold text-ink">
              {price.perBundle !== null ? formatPrice(price.perBundle) : "Harga menyusul"}
              <span className="text-[11px] font-semibold text-muted"> / bundling</span>
            </p>
            <p className="mt-0.5 text-[11px] font-semibold text-muted">
              {price.note} Barangnya dipesan dengan harga satuan.
            </p>
            {price.extras > 0 ? (
              <p className="mt-0.5 text-[10px] font-semibold text-muted">{priceExtraNote(price.extras, null)}</p>
            ) : null}
          </div>
        ) : (
          <div>
            <p className="flex flex-wrap items-baseline gap-x-1.5">
              <span className={`text-base font-extrabold ${price.perBundle !== null ? "text-shopee" : "text-muted"}`}>
                {price.perBundle !== null ? formatPrice(price.perBundle) : "Harga dihitung saat checkout"}
              </span>
              {price.perBundle !== null ? (
                <span className="text-[11px] font-semibold text-muted">/ bundling</span>
              ) : null}
              {price.separate !== null ? (
                <span className="text-[11px] font-semibold text-muted line-through">
                  {formatPrice(price.separate)}
                </span>
              ) : null}
            </p>
            {price.saving > 0 ? (
              <p className="mt-1 inline-flex rounded-md bg-leaf/10 px-1.5 py-0.5 text-[10px] font-extrabold text-leaf">
                Hemat {formatPrice(price.saving)}
                {price.savingPercent > 0 ? ` (${price.savingPercent}%)` : ""}
              </p>
            ) : null}
            {(price.kind === "bundle" || price.kind === "snapshot") && price.extras > 0 ? (
              <p className="mt-0.5 text-[10px] font-semibold text-muted">
                {/* D14-20: kartu tak tahu asal tambahannya (ukuran/warna) — tanpa subjek. */}
                {priceExtraNote(price.extras, null)}
              </p>
            ) : null}
          </div>
        )}
        {refreshing ? (
          <p className="mt-1 text-[10px] font-semibold text-muted">Memperbarui harga…</p>
        ) : null}
      </div>

      {/* Jumlah bundel + ubah pilihan */}
      <div className="mt-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-bold text-ink">Jumlah bundling</p>
          {bundle.slug ? (
            // `ganti` = kunci entri ini: halaman bundel membuka pilihan & jumlah
            // entri ini lalu MENGGANTINYA (bukan menambah entri kedua).
            <Link
              href={`/bundling/${bundle.slug}?ganti=${encodeURIComponent(bundle.key)}`}
              className="mt-0.5 inline-flex items-center gap-1 text-xs font-semibold text-shopee"
            >
              <Pencil size={11} />
              Ubah pilihan
            </Link>
          ) : null}
        </div>
        <QuantityStepper value={bundle.qty} max={MAX_BUNDLE_QTY} onChange={onQtyChange} />
      </div>

      {/* Total bundel */}
      <div className="mt-3 flex items-center justify-between border-t border-neutral-100 pt-3">
        <span className="text-sm font-bold text-muted">Total bundling</span>
        <span className={`text-base font-extrabold ${unit !== null ? "text-shopee" : "text-muted"}`}>
          {unit !== null ? formatPrice(unit * bundle.qty) : "—"}
        </span>
      </div>

      {/* DP — nominalnya tak dihitung di sini: setelan DP komponen tak ikut
          potret keranjang bundel, dan angka yang salah lebih buruk daripada
          tanpa angka. Produk yang juga ada di keranjang biasa menghitung baris
          bundelnya di kartu produknya. */}
      {bundle.productType === "preorder" ? (
        <p className="mt-2 rounded-lg border border-marigold/30 bg-marigold/10 p-2.5 text-[11px] font-semibold text-muted">
          Produk Pre-Order yang wajib DP: tagihan DP otomatis terbit setelah pesanan dibuat — bayar
          pakai saldo atau transfer dari menu Tagihan.
        </p>
      ) : null}
    </div>
  );
}
